import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { refreshToken, isTokenExpired, isTokenExpiringSoon } from '../services/authService';

interface DecodedToken { 
  'X-SC-Role': string;
  'X-SC-ID': string;
  'X-SC-Session-ID': string;
  'X-SC-Nonce': string;
  'X-SC-TokenType': string;
  exp: number;
  iss: string;
  aud: string;
}

export const useAuth = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userId');
    localStorage.removeItem('accessTokenExpiredAt');
    localStorage.removeItem('refreshTokenExpiredAt');
    setIsAuthenticated(false);
  }, []);

  const attemptTokenRefresh = useCallback(async (): Promise<boolean> => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    const username = localStorage.getItem('username');

    if (!refreshTokenValue || !username) {
      return false;
    }

    try {
      const response = await refreshToken({
        token: refreshTokenValue,
        name: username
      });

      // Update tokens in localStorage
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('accessTokenExpiredAt', response.accessTokenExpiredAt);
      localStorage.setItem('refreshTokenExpiredAt', response.refreshTokenExpiredAt);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      clearAuthData();
      navigate('/login');
      return;
    }
    
    try {
      const decoded: DecodedToken = jwtDecode(token);
      
      // Check if user has valid role (Admin, SuperAgent, or Agent)
      const validRoles = ['Admin', 'SuperAgent', 'Agent'];
      if (!validRoles.includes(decoded['X-SC-Role'])) {
        clearAuthData();
        navigate('/login');
        return;
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        // Try to refresh the token
        const refreshSuccess = await attemptTokenRefresh();
        if (!refreshSuccess) {
          clearAuthData();
          navigate('/login');
          return;
        }
        // Token was refreshed, continue with authentication
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // Check if token is expiring soon and refresh proactively
      if (isTokenExpiringSoon(token, 5)) {
        attemptTokenRefresh().catch(console.error);
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error('Token decode error:', error);
      clearAuthData();
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  }, [attemptTokenRefresh, clearAuthData, navigate]);
  
  useEffect(() => {
    void checkAuthStatus();
  }, [checkAuthStatus]);

  return {
    isAuthenticated,
    isLoading,
    checkAuthStatus,
    clearAuthData
  };
};
